// Date range selections for Analytics, modeled on YouTube Studio's picker.
// A selection is stored (not its dates) so "Last 28 days" stays current.

// Versioned so everyone starts on the current month once it became the
// default, instead of a range remembered from before.
const STORAGE_KEY = 'analytics.range.v2';
export const DAY_OPTIONS = [7, 28, 90, 365];

function pad(value) {
  return String(value).padStart(2, '0');
}

function lastDayOfMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

// Days are Pacific time, as YouTube reports them and the server counts them,
// so a month starts at Pacific midnight wherever the viewer is.
function pacificDate(daysAgo = 0) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date());
  const part = (type) => Number(parts.find((item) => item.type === type).value);
  const date = new Date(Date.UTC(part('year'), part('month') - 1, part('day') - daysAgo));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
}

// The server leaves out the last 3 days while YouTube finalizes them.
const FINALIZED_LAG_DAYS = 3;

/** The `count` months up to the month `daysAgo` days back, newest first. */
export function recentMonths(count = 3, daysAgo = 0) {
  const { year, month } = pacificDate(daysAgo);
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(Date.UTC(year, month - 1 - index, 1));
    return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1 };
  });
}

export function recentYears(count = 3) {
  const { year } = pacificDate();
  return Array.from({ length: count }, (_, index) => year - index);
}

export function monthLabel(year, month) {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  });
}

export function selectionLabel(selection) {
  switch (selection.type) {
    case 'days':
      return `Last ${selection.days} days`;
    case 'lifetime':
      return 'Lifetime';
    case 'month':
      return monthLabel(selection.year, selection.month);
    case 'year':
      return String(selection.year);
    case 'custom':
      return `${selection.from} – ${selection.to}`;
    default:
      return 'Last 28 days';
  }
}

// Query parameters for the API. The server caps every range at the last
// finalized day.
export function selectionQuery(selection) {
  switch (selection.type) {
    case 'lifetime':
      return { lifetime: 'true' };
    case 'month':
      return {
        from: `${selection.year}-${pad(selection.month)}-01`,
        to: `${selection.year}-${pad(selection.month)}-${pad(lastDayOfMonth(selection.year, selection.month))}`,
      };
    case 'year':
      return { from: `${selection.year}-01-01`, to: `${selection.year}-12-31` };
    case 'custom':
      return { from: selection.from, to: selection.to };
    default:
      return { days: selection.days };
  }
}

// The month of the last finalized day, until the user picks another range.
// That is the current month except in its first three days, which have no
// finalized numbers yet.
export function defaultSelection() {
  return { type: 'month', ...recentMonths(1, FINALIZED_LAG_DAYS)[0] };
}

export function sameSelection(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function loadSelection() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved?.type) return saved;
  } catch (_) {
    // Storage can be unavailable; fall back to the default range.
  }
  return defaultSelection();
}

export function saveSelection(selection) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch (_) {
    // Not remembering the range is fine.
  }
}
